import type { DimensionGroup, MeasureGroup } from "../../../../../../../../api/types/svTypes.ts";
import type { ReactNode } from "react";

export interface FieldTreeGroup<T> {
  id: string;
  caption: string;
  fieldGroupCaption?: string;
  actions?: ReactNode;
  fields: T[];
  groups: FieldTreeGroup<T>[];
}

export const createDimensionTreeGroups = <T extends { name: string }>(
  fields: T[],
  groups: DimensionGroup[]
): FieldTreeGroup<T>[] => {
  const byName = new Map(fields.map((field) => [field.name, field]));
  const buildGroups = (items: DimensionGroup[], parentId: string): FieldTreeGroup<T>[] =>
    items.flatMap((group) => {
      const id = `${parentId}/${encodeURIComponent(group.caption)}`;
      const groupFields = group.dimensions.flatMap((name) => {
        const field = byName.get(name);
        return field ? [field] : [];
      });
      const children = buildGroups(group.groups ?? [], id);
      return groupFields.length || children.length
        ? [{ id, caption: group.caption, fields: groupFields, groups: children }]
        : [];
    });
  return buildGroups(groups, "");
};

export const createMeasureTreeGroups = <T extends { name: string }>(
  fields: T[],
  groups: (MeasureGroup & { groups?: { id: string; caption: string; names: string[] }[] })[]
): FieldTreeGroup<T>[] => {
  const byName = new Map(fields.map((field, index) => [field.name, { field, index }]));
  const getFields = (names: string[]): T[] =>
    [...new Set(names)]
      .flatMap((name) => {
        const item = byName.get(name);
        return item ? [item] : [];
      })
      .sort((a, b) => a.index - b.index)
      .map(({ field }) => field);
  return groups.flatMap((group) => {
    const groupFields = getFields(group.names);
    const children = (group.groups ?? []).map((subgroup) => ({
      id: subgroup.id,
      caption: subgroup.caption,
      fieldGroupCaption: group.caption,
      fields: getFields(subgroup.names),
      groups: [],
    }));
    return groupFields.length || children.length
      ? [{ id: `/${encodeURIComponent(group.caption)}`, caption: group.caption, fields: groupFields, groups: children }]
      : [];
  });
};
